import React, { useState, useEffect } from "react";
import { Button } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { postApi } from "../../../Services/apiService";
import { toast } from "react-toastify";


function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordActive, setPasswordActive] = useState(false);
  const [confirmPasswordActive, setConfirmPasswordActive] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({
    password: "",
    confirmPassword: ""
  });
  const [touched, setTouched] = useState({
    password: false,
    confirmPassword: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token");
  const email = searchParams.get("email");

  useEffect(() => {
    if (!token || !email) {
      toast.error("Invalid password reset link. Please use the link sent to your email.");
      navigate("/forgot-password");
    }
  }, [token, email, navigate]);

  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

  // Validate password whenever it changes
  const validatePassword = (password) => {
    // const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    const passwordRegex = /^.{6,}$/;
    if (!password) return "Password is required";
    if (!passwordRegex.test(password)) return "Password must be at least 6 characters";
    return "";
  };

  // Validate confirm password whenever it or password changes
  const validateConfirmPassword = (confirmPassword) => {
    if (!confirmPassword) return "Please confirm your password";
    if (password !== confirmPassword) return "Passwords do not match";
    return "";
  };

  // Real-time validation for both fields
  useEffect(() => {
    const passwordError = validatePassword(password);
    const confirmPasswordError = validateConfirmPassword(confirmPassword);
    
    setErrors({
      password: passwordError,
      confirmPassword: confirmPasswordError
    });
  }, [password, confirmPassword]);

  const handleBlur = (field) => {
    setTouched({...touched, [field]: true});
  };

  const validateOnSubmit = () => {
    const passwordError = validatePassword(password);
    const confirmPasswordError = validateConfirmPassword(confirmPassword);
    
    setErrors({
      password: passwordError,
      confirmPassword: confirmPasswordError
    });
    
    setTouched({
      password: true,
      confirmPassword: true
    });

    return !passwordError && !confirmPasswordError;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateOnSubmit()) return;
    if (!token || !email) {
      toast.error("Invalid reset link");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await postApi("/reset-password", {
        email: decodeURIComponent(email),
        token: token,
        password: password,
        password_confirmation: confirmPassword
      });

      if (response?.success) {
        toast.success(response.msg || "Password reset successfully");
        setTimeout(() => navigate("/login"), 2000);
      } else {
        toast.error(response?.message || "Failed to reset password");
      }
    } catch (error) {
      console.error("Reset password error:", error);
      toast.error(error.response?.data?.message || error.message || "An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token || !email) {
    return (
      <div className="sign-up-main login">
        <div className="sign-left">
          <div className="alert alert-danger">
            Invalid password reset link. Please use the link sent to your email.
          </div>
          <Button 
            variant="primary" 
            onClick={() => navigate('/forgot-password')}
            className="mt-3"
          >
            Request New Reset Link
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="sign-up-main login">
      {/* <ToastContainer position="top-right" autoClose={5000} /> */}
      <div className="sign-right">
        <div className="logo" onClick={() => navigate("/")}>
          <h5>We Make IT <span className="line"></span></h5>
          <h2 className="mb-0">
            Happen<span>.</span>
          </h2>
          <h2 className="mb-0 green-text">Better<span>.</span></h2>
        </div>
      </div>
      <div className="sign-left">
        <div className="start-rifgr-sec">
          <div className="text-center mb-4">
            <img 
              src={window.getPublicUrl("/assets/images/logo.svg")} 
              alt="Company Logo"
            />
          </div>
          <div className="login-container">
            <h2>Reset your password</h2>
            <form onSubmit={handleSubmit}>
              <div className={`form-group position-relative ${passwordActive || password ? "active" : ""}`}>
                <label>New Password</label>
                <input
                  className={`form-control ${errors.password && touched.password ? "is-invalid" : ""}`}
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setPasswordActive(true)}
                  onBlur={() => { 
                    setPasswordActive(false); 
                    handleBlur('password');
                  }}
                />
                <span 
                  className="eye-icon" 
                  style={{ cursor: "pointer" }} 
                  onClick={togglePasswordVisibility}
                >
                  <img
                    src={
                      showPassword
                        ? window.getPublicUrl("/assets/images/Eye.svg")
                        : window.getPublicUrl("/assets/images/Eye-closed.svg")
                    }
                    alt="Toggle Password"
                  />
                </span>
                {errors.password && touched.password && (
                  <div className="invalid-feedback d-block">{errors.password}</div>
                )}
              </div>

              <div className={`form-group position-relative ${confirmPasswordActive || confirmPassword ? "active" : ""}`}>
                <label>Confirm Password</label>
                <input
                  className={`form-control ${errors.confirmPassword && touched.confirmPassword ? "is-invalid" : ""}`}
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onFocus={() => setConfirmPasswordActive(true)}
                  onBlur={() => { 
                    setConfirmPasswordActive(false); 
                    handleBlur('confirmPassword');
                  }}
                />
                <span 
                  className="eye-icon" 
                  style={{ cursor: "pointer" }} 
                  onClick={toggleConfirmPasswordVisibility}
                >
                  <img
                    src={
                      showConfirmPassword
                        ? window.getPublicUrl("/assets/images/Eye.svg")
                        : window.getPublicUrl("/assets/images/Eye-closed.svg")
                    }
                    alt="Toggle Confirm Password"
                  />
                </span>
                {errors.confirmPassword && touched.confirmPassword && (
                  <div className="invalid-feedback d-block">{errors.confirmPassword}</div>
                )}
              </div>

              <Button 
                type="submit" 
                variant="sign-in" 
                disabled={isSubmitting}
                className="w-100 mt-3"
              >
                {isSubmitting ? (
                  <>
                    <span 
                      className="spinner-border spinner-border-sm me-2" 
                      role="status" 
                      aria-hidden="true"
                    ></span>
                    Resetting...
                  </>
                ) : (
                  "Reset Password"
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;